"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RecurringOrderService, {
  RECURRING_STATUS,
  RECURRING_STATUS_NAME,
  FREQUENCY_TYPE,
  FREQUENCY_TYPE_NAME,
  RecurringOrder,
} from "../services/recurring-order-service";
import RecurringOrderTable, {
  RecurringOrderRow,
} from "../components/recurring-order-table";
import RecurringOrderCreateModal, {
  CreateFormData,
} from "../components/recurring-order-create-modal";
import RecurringOrderDetailModal from "../components/recurring-order-detail-modal";
import RecurringAssignStaffModal from "../components/recurring-assign-staff-modal";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import BranchService, { Branch } from "../../branch/services/branch-service";
import { CustomerService } from "../../customer/services/customer-service";
import { ServiceMapService } from "../../order/services/service-map-service";
import { ServiceAddonService } from "../../order/services/service-addon-service";

/** Extract array from API response */
function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const arr = raw.list ?? raw.data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

const RecurringOrdersIndexPage: React.FC = () => {
  // State
  const [rows, setRows] = useState<RecurringOrderRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [customers, setCustomers] = useState<
    Array<{ id: number; name: string; phone?: string; address?: string }>
  >([]);
  const [services, setServices] = useState<
    Array<{ id: number; name: string; price?: number }>
  >([]);
  const [addons, setAddons] = useState<
    Array<{ id: number; name: string; price?: number }>
  >([]);
  const [selectedBranchId, setSelectedBranchId] = useState<
    number | undefined
  >();
  const [filterStatus, setFilterStatus] = useState<number | undefined>();
  const [filterFrequency, setFilterFrequency] = useState<number | undefined>();
  const [customerNameFilter, setCustomerNameFilter] = useState("");

  // Modals
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState<RecurringOrder | null>(null);
  const [detailRecurring, setDetailRecurring] = useState<RecurringOrder | null>(
    null,
  );
  const [assigningStaff, setAssigningStaff] = useState<RecurringOrder | null>(null);

  // Services
  const { fetch: listRecurring, loading: loadingList } =
    RecurringOrderService.list();
  const { fetch: createRecurring, loading: creating } =
    RecurringOrderService.create();
  const { fetch: updateRecurring, loading: updating } =
    RecurringOrderService.update();
  const { fetch: assignStaff, loading: assigningStaffLoading } =
    RecurringOrderService.assignStaff();
  const { fetch: toggleStatus } = RecurringOrderService.toggleStatus();
  const { fetch: endRecurring } = RecurringOrderService.end();
  const { fetch: fetchDetail } = RecurringOrderService.detail();
  const { fetch: listBranches } = BranchService.list();
  const { fetch: listCustomers } = CustomerService.list();
  const { fetch: fetchServices } = ServiceMapService.listActive();
  const { fetch: fetchAddons } = ServiceAddonService.listActive();

  const listRef = useRef(listRecurring);
  useEffect(() => {
    listRef.current = listRecurring;
  }, [listRecurring]);

  // Load branches + customers + services + addons
  useEffect(() => {
    (async () => {
      const branchRes = await listBranches({
        status: 1,
        company_id: 2000,
      } as any);
      if (branchRes && isSuccess(branchRes)) {
        const branchList = extractList(branchRes.data);
        setBranches(branchList);
        if (branchList.length > 0 && !selectedBranchId) {
          setSelectedBranchId(branchList[0].id);
        }
      }
    })();
    (async () => {
      const custRes = await listCustomers({ page: 1, limit: 500 } as any);
      if (custRes && isSuccess(custRes)) {
        setCustomers(extractList(custRes.data));
      }
    })();
  }, []);

  // Load services + addons khi branch thay đổi
  useEffect(() => {
    if (!selectedBranchId) return;
    (async () => {
      const res = await fetchServices({
        status: 1,
        branch_id: selectedBranchId,
        limit: 1000,
      } as any);
      if (res && isSuccess(res)) {
        const list = extractList(res.data);
        setServices(
          list.map((s: any) => ({
            id: s.id,
            name:
              [s.service?.name, s.serviceType?.name || s.service_type?.name]
                .filter(Boolean)
                .join(" - ") ||
              s.name ||
              s.service_name ||
              `DV #${s.id}`,
            price: Number(s.price) || 0,
          })),
        );
      }
    })();
    (async () => {
      const res = await fetchAddons({
        status: 1,
        branch_id: selectedBranchId,
        limit: 1000,
      } as any);
      if (res && isSuccess(res)) {
        const list = extractList(res.data);
        setAddons(
          list.map((a: any) => ({
            id: a.id,
            name: a.name || a.addon_name || `Addon #${a.id}`,
            price: a.price,
          })),
        );
      }
    })();
  }, [selectedBranchId]);

  // Load recurring orders
  const load = useCallback(async () => {
    const params: any = {};
    if (selectedBranchId) params.branch_id = selectedBranchId;
    if (filterStatus !== undefined) params.status = filterStatus;
    if (filterFrequency) params.frequency_type = filterFrequency;

    const res = await listRef.current(params);
    if (res && isSuccess(res)) {
      const list = extractList(res.data);
      setRows(
        list.map((r: RecurringOrder) => ({
          id: r.id,
          customer_id: r.customer_id,
          customer_name: customers.find(
            (c) => Number(c.id) === Number(r.customer_id),
          )?.name,
          branch_id: r.branch_id,
          branch_name: branches.find(
            (b) => Number(b.id) === Number(r.branch_id),
          )?.name,
          frequency_type: r.frequency_type,
          frequency_value: r.frequency_value,
          next_order_date: r.next_order_date,
          preferred_time: r.preferred_time,
          total_orders_created: r.total_orders_created,
          requires_counting: (r as any).requires_counting,
          status: r.status,
          created_at: r.created_at,
        })),
      );
    }
  }, [selectedBranchId, filterStatus, filterFrequency, customers, branches]);

  useEffect(() => {
    void load();
  }, [load]);

  // Lọc theo tên khách — client-side vì `rows` đã tải hết (không phân trang), không cần gọi thêm API.
  const filteredRows = useMemo(() => {
    const key = customerNameFilter.trim().toLowerCase();
    if (!key) return rows;
    return rows.filter((r) => (r.customer_name || "").toLowerCase().includes(key));
  }, [rows, customerNameFilter]);

  // Counts by status — tính trên danh sách ĐÃ LỌC để khớp với bảng đang hiển thị.
  const countByStatus = useMemo(() => {
    const counts: Record<number, number> = {};
    filteredRows.forEach((r) => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [filteredRows]);

  // Handlers
  const onCreate = async (data: CreateFormData) => {
    try {
      const res = await createRecurring(data);
      if (res && isSuccess(res)) {
        showToast({ message: "Tạo đơn định kỳ thành công", type: "success" });
        setOpenCreate(false);
        void load();
      } else {
        showToast({
          message: (res as any)?.message || "Tạo đơn định kỳ thất bại",
          type: "error",
        });
      }
    } catch (err) {
      showToast({ message: "Lỗi khi tạo đơn định kỳ", type: "error" });
    }
  };

  const onUpdate = async (data: CreateFormData) => {
    if (!editing) return;
    // BE bỏ qua customer_id / branch_id / start_date khi update — chỉ gửi phần sửa được
    const { customer_id, branch_id, start_date, ...rest } = data;
    void customer_id;
    void branch_id;
    void start_date;
    try {
      const res = await updateRecurring(editing.id, rest);
      if (res && isSuccess(res)) {
        showToast({ message: "Đã cập nhật đơn định kỳ", type: "success" });
        setEditing(null);
        void load();
      } else {
        showToast({
          message: (res as any)?.message || "Cập nhật thất bại",
          type: "error",
        });
      }
    } catch (err) {
      showToast({ message: "Lỗi khi cập nhật đơn định kỳ", type: "error" });
    }
  };

  const onEdit = async (row: RecurringOrderRow) => {
    const res = await fetchDetail(row.id);
    if (res && isSuccess(res)) {
      setEditing(res.data as any);
    } else {
      showToast({ message: "Không lấy được chi tiết đơn định kỳ", type: "error" });
    }
  };

  const onAssignStaff = async (row: RecurringOrderRow) => {
    const res = await fetchDetail(row.id);
    if (res && isSuccess(res)) {
      setAssigningStaff(res.data as any);
    } else {
      showToast({ message: "Không lấy được chi tiết đơn định kỳ", type: "error" });
    }
  };

  const onSubmitAssignStaff = async (
    id: number,
    payload: {
      pickup_driver_ids: number[];
      counter_ids: number[];
      delivery_driver_ids: number[];
      delivery_counter_ids: number[];
      pickup_vehicle_id: number | null;
      delivery_vehicle_id: number | null;
    },
  ) => {
    const res = await assignStaff(id, payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Đã gán nhân sự mặc định", type: "success" });
      setAssigningStaff(null);
      void load();
    } else {
      showToast({
        message: (res as any)?.message || "Gán nhân sự thất bại",
        type: "error",
      });
    }
  };

  const onViewDetail = async (row: RecurringOrderRow) => {
    const res = await fetchDetail(row.id);
    if (res && isSuccess(res)) {
      setDetailRecurring(res.data as any);
    }
  };

  const onToggleStatus = async (row: RecurringOrderRow) => {
    const action =
      row.status === RECURRING_STATUS.ACTIVE ? "tạm dừng" : "kích hoạt";
    if (!window.confirm(`Xác nhận ${action} đơn định kỳ #${row.id}?`)) return;
    const res = await toggleStatus(row.id);
    if (res && isSuccess(res)) {
      showToast({ message: `Đã ${action} đơn định kỳ`, type: "success" });
      void load();
    } else {
      showToast({
        message: (res as any)?.message || "Thao tác thất bại",
        type: "error",
      });
    }
  };

  const onEnd = async (row: RecurringOrderRow) => {
    if (
      !window.confirm(
        `Xác nhận KẾT THÚC VĨNH VIỄN đơn định kỳ #${row.id}? Hành động này không thể hoàn tác.`,
      )
    )
      return;
    const res = await endRecurring(row.id);
    if (res && isSuccess(res)) {
      showToast({ message: "Đã kết thúc đơn định kỳ", type: "success" });
      void load();
    } else {
      showToast({
        message: (res as any)?.message || "Thao tác thất bại",
        type: "error",
      });
    }
  };

  const onDetailToggle = async (id: number) => {
    const res = await toggleStatus(id);
    if (res && isSuccess(res)) {
      showToast({ message: "Đã cập nhật trạng thái", type: "success" });
      setDetailRecurring(null);
      void load();
    }
  };

  const onDetailEnd = async (id: number) => {
    if (!window.confirm("Xác nhận kết thúc vĩnh viễn?")) return;
    const res = await endRecurring(id);
    if (res && isSuccess(res)) {
      showToast({ message: "Đã kết thúc", type: "success" });
      setDetailRecurring(null);
      void load();
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-blue-600 tracking-tight">
            Đơn hàng định kỳ
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Quản lý lịch tạo đơn tự động cho khách hàng
          </p>
        </div>

        <button
          type="button"
          onClick={() => setOpenCreate(true)}
          className="group inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white shadow-lg bg-blue-600 shadow-blue-200 hover:bg-blue-700 hover:-translate-y-0.5 transition-all cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5 transition-transform group-hover:rotate-90"
          >
            <path
              fillRule="evenodd"
              d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z"
              clipRule="evenodd"
            />
          </svg>
          <span>TẠO ĐƠN ĐỊNH KỲ</span>
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
        {/* FILTERS */}
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col xl:flex-row xl:items-center justify-between gap-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              >
                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
              </svg>
              <input
                type="text"
                value={customerNameFilter}
                onChange={(e) => setCustomerNameFilter(e.target.value)}
                placeholder="Tìm theo tên khách hàng..."
                className="rounded-xl border border-gray-200 bg-white pl-9 pr-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm placeholder:font-normal placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all w-56"
              />
            </div>

            <select
              value={selectedBranchId ?? ""}
              onChange={(e) =>
                setSelectedBranchId(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all cursor-pointer"
            >
              <option value="">Tất cả chi nhánh</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>

            <select
              value={filterStatus ?? ""}
              onChange={(e) =>
                setFilterStatus(
                  e.target.value !== "" ? Number(e.target.value) : undefined,
                )
              }
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all cursor-pointer"
            >
              <option value="">Tất cả trạng thái</option>
              {Object.entries(RECURRING_STATUS_NAME).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>

            <select
              value={filterFrequency ?? ""}
              onChange={(e) =>
                setFilterFrequency(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all cursor-pointer"
            >
              <option value="">Tất cả tần suất</option>
              {Object.entries(FREQUENCY_TYPE_NAME).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* STATUS CHIPS */}
        <div className="px-6 py-3 border-b border-gray-50 bg-gray-50/30 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Tổng quan:
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            Hoạt động: {countByStatus[RECURRING_STATUS.ACTIVE] || 0}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-50 px-3 py-1 text-xs font-bold text-yellow-700">
            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
            Tạm dừng: {countByStatus[RECURRING_STATUS.PAUSED] || 0}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
            Kết thúc: {countByStatus[RECURRING_STATUS.ENDED] || 0}
          </span>
        </div>

        {/* TABLE */}
        <div className="p-0">
          <RecurringOrderTable
            rows={filteredRows}
            isLoading={loadingList}
            startIndex={1}
            onViewDetail={onViewDetail}
            onEdit={onEdit}
            onAssignStaff={onAssignStaff}
            onToggleStatus={onToggleStatus}
            onEnd={onEnd}
          />
        </div>

        {/* FOOTER */}
        <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 font-medium">
            {filteredRows.length > 0
              ? customerNameFilter.trim()
                ? `${filteredRows.length}/${rows.length} đơn định kỳ khớp "${customerNameFilter.trim()}"`
                : `Tổng cộng ${filteredRows.length} đơn định kỳ`
              : rows.length > 0
                ? "Không có đơn định kỳ nào khớp tên khách"
                : "Chưa có đơn định kỳ nào"}
          </div>
        </div>
      </div>

      {/* MODALS */}
      <RecurringOrderCreateModal
        open={openCreate || !!editing}
        mode={editing ? "edit" : "create"}
        initial={editing}
        onClose={() => {
          setOpenCreate(false);
          setEditing(null);
        }}
        onSubmit={editing ? onUpdate : onCreate}
        loading={editing ? updating : creating}
        customers={customers}
        branches={branches}
        services={services}
        addons={addons}
      />

      <RecurringOrderDetailModal
        open={!!detailRecurring}
        onClose={() => setDetailRecurring(null)}
        recurring={detailRecurring}
        onToggleStatus={onDetailToggle}
        onEnd={onDetailEnd}
        onEdit={(rec) => {
          setDetailRecurring(null);
          setEditing(rec);
        }}
        customerName={
          detailRecurring
            ? customers.find(
                (c) => Number(c.id) === Number(detailRecurring.customer_id),
              )?.name
            : undefined
        }
        branchName={
          detailRecurring
            ? branches.find(
                (b) => Number(b.id) === Number(detailRecurring.branch_id),
              )?.name
            : undefined
        }
      />

      <RecurringAssignStaffModal
        open={!!assigningStaff}
        onClose={() => setAssigningStaff(null)}
        recurring={assigningStaff}
        branchName={
          assigningStaff
            ? branches.find(
                (b) => Number(b.id) === Number(assigningStaff.branch_id),
              )?.name
            : undefined
        }
        onSubmit={onSubmitAssignStaff}
        loading={assigningStaffLoading}
      />
    </div>
  );
};

export default RecurringOrdersIndexPage;
